import React, { FC, MouseEvent, useContext, useMemo, useState } from 'react';

import { Popover, PopoverPosition } from '@mui/material';

// components
import IconMenuButton from '../../../../../components/buttons/iconEditorMenuButton';
import EmojiPicker from '../../../../../components/shared/EmojiPicker/EmojiPicker';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

// context
import { EditorContext } from '../../../../../context/EditorContext';

//hooks
import useAttributesQuery from '../../../../../hooks/filters/useAttributesQuery';
//hooks
import useAnalytics from '../../../../../hooks/helper/useAnalytics';
import useOutsideClickDetector from '../../../../../hooks/useOutsideClickDetector';

//models
import { FieldType } from '../../../../../models/contact_attributes.model';
import { EmojiClickData } from '../../../../../models/emoji.model';

import CMSPlaceholderDropdown from '../CMSPlaceholderDropdown';
import DatasetIconDropDown from '../DatasetIconDropDown';
import AttributePlaceholderModal from './AttributePlaceholder/AttributePlaceholderModal/AttributePlaceholderModal';

interface SlateInputMenuProps {
    insertDataset: ((id?: number) => void) | null;
    insertCMSPlaceholder: ((id: string) => void) | null;
    insertEmoji: ((emoji: string) => void) | null;
    insertUserAttributePlaceholder?: (
        defaultValue: string,
        userAttribute: string,
    ) => void;
    isDisabled: boolean;
}

const SlateInputMenu: FC<React.PropsWithChildren<SlateInputMenuProps>> = ({
    insertDataset,
    insertCMSPlaceholder,
    insertEmoji,
    isDisabled,
    insertUserAttributePlaceholder,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const [showPicker, setShowPicker] = useState<PopoverPosition | undefined>();
    const [placeholderAnchorPosition, setPlaceholderAnchorPosition] = useState<
        PopoverPosition | undefined
    >(undefined);

    const [attributePlaceholderModal, setAttributePlaceholderModal] =
        useState<boolean>(false);

    const { data: attributesData } = useAttributesQuery({
        is_active: true,
        sort_by_name: true,
        attribute_type: FieldType.STR,
    });

    const { track } = useAnalytics();

    const onEmojiClick = (event: MouseEvent, emojiData: EmojiClickData) => {
        if (insertEmoji) {
            insertEmoji(emojiData.emoji);
            track('formula_emoji_inserted');
        }
        setShowPicker(undefined);
    };

    const handleClickPlaceholder = (e: MouseEvent<HTMLDivElement>) => {
        setPlaceholderAnchorPosition({
            top: (e.clientY ?? 0) + 10,
            left: e.clientX ?? 0,
        });
    };

    const handleAttributePlaceholder = () => {
        setAttributePlaceholderModal(true);
    };

    const pickerRef = useOutsideClickDetector(() => setShowPicker(undefined));
    const placeholderRef = useOutsideClickDetector(() =>
        setPlaceholderAnchorPosition(undefined),
    );

    const handleSelectPlaceholder = (id: string) => {
        setPlaceholderAnchorPosition(undefined);
        insertCMSPlaceholder?.(id);
    };

    const isCMSDisabled = isDisabled || Boolean(!formula?.cms_type);

    const showCMSPlaceholder = useMemo<boolean>(() => {
        return !!formula?.cms_type;
    }, [formula]);

    const showDatasetIcon = useMemo<boolean>(() => {
        return !!formula?.dataset_id;
    }, [formula]);

    const showUserAttributePlaceholderIcon = useMemo<boolean>(() => {
        return !!attributesData?.items?.length;
    }, [attributesData]);

    return (
        <>
            <EditorButtons contentEditable={false}>
                {showUserAttributePlaceholderIcon && (
                    <IconMenuButton
                        Icon={ICONS.attributePlaceholder}
                        action={handleAttributePlaceholder}
                        isDisabled={isDisabled}
                        tooltip="Insert user attribute placeholder"
                    />
                )}
                {insertDataset && showDatasetIcon && (
                    <DatasetIconDropDown
                        showIcon
                        isIconDisabled={isDisabled}
                        handleDatasetFieldClick={(id) => {
                            if (!isDisabled) {
                                insertDataset?.(id);
                            }
                        }}
                    />
                )}
                {insertCMSPlaceholder && showCMSPlaceholder && (
                    <div ref={placeholderRef}>
                        <IconMenuButton
                            Icon={ICONS.cart}
                            isDisabled={isCMSDisabled}
                            action={handleClickPlaceholder}
                            tooltip={
                                isCMSDisabled
                                    ? 'Select your CMS type to enable this option'
                                    : 'Add a reference to an item in the cart'
                            }
                        />
                    </div>
                )}
                {insertEmoji && (
                    <div ref={pickerRef}>
                        <IconMenuButton
                            Icon={ICONS.emoji}
                            isDisabled={isDisabled}
                            action={(e) => {
                                setShowPicker({
                                    top: (e.clientY ?? 0) + 10,
                                    left: e.clientX ?? 0,
                                });
                            }}
                            tooltip="Insert Emoji"
                        />
                        {showPicker ? (
                            <Popover
                                open
                                anchorReference="anchorPosition"
                                anchorPosition={showPicker}
                                onClose={() => setShowPicker(undefined)}
                            >
                                <EmojiPicker
                                    preload={true}
                                    onEmojiClick={onEmojiClick}
                                />
                            </Popover>
                        ) : null}
                    </div>
                )}
            </EditorButtons>
            {placeholderAnchorPosition && (
                <CMSPlaceholderDropdown
                    anchorPosition={placeholderAnchorPosition}
                    handleClick={handleSelectPlaceholder}
                />
            )}
            {attributePlaceholderModal && (
                <AttributePlaceholderModal
                    handleClose={() => setAttributePlaceholderModal(false)}
                    insertUserAttributePlaceholder={
                        insertUserAttributePlaceholder
                    }
                />
            )}
        </>
    );
};

export default SlateInputMenu;

const EditorButtons = styled.div({
    userSelect: 'none',
    display: 'flex',
    gap: aampeTheme.spacing(1),
    width: 'fit-content',
    justifyContent: 'center',
    alignItems: 'flex-end',
    margin: aampeTheme.spacing(0, 1),
});
